import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import * as CryptoJS from 'crypto-js';

declare global {
  interface Window {
    _mtm: any[];
    _paq: any[];
    Matomo: any;
  }
}

interface TrackingData {
  idSite: number;
  idVisit: string;
  visitIp: string;
  visitorId: string;
  fingerprint: string;
  actionDetails: any[];
}

interface TrackingResponse {
  success: boolean;
  message: string;
  data: TrackingData;
}

interface EncryptionConfig {
  key: string;
  algorithm: string;
}

interface EnvironmentWithEncryption {
  encryption: EncryptionConfig;
  matomo: any;
  api: any;
  production: boolean;
}

const env = environment as EnvironmentWithEncryption;

@Injectable({
  providedIn: 'root',
})
export class MatomoTrackingService {
  public static instance: MatomoTrackingService;

  private origen: string = 'directo';
  private matomoLoaded = false;
  private matomoEnabled = true;
  private visitorType: 'new' | 'returning' = 'new';
  private loadAttempts = 0;
  private maxLoadAttempts = 3;
  private currentSiteId: string;

  // Datos de tracking obtenidos del backend
  private trackingData: {
    idVisit: string;
    visitIp: string;
    visitorId: string;
    idSite: number;
  } | null = null;

  private readonly MATOMO_CONFIG = {
    url:
      environment.matomo.scriptUrl ||
      'https://cdn.matomo.cloud/sahulfb.matomo.cloud/container_mXBKleXN.js',
    apiUrl:
      environment.matomo.apiUrl || 'https://sahulfb.matomo.cloud/index.php',
    siteId: environment.matomo.siteId || '6',
    enabled: environment.matomo.enabled,
  };

  constructor(private router: Router, private http: HttpClient) {
    MatomoTrackingService.instance = this;
    this.currentSiteId = this.MATOMO_CONFIG.siteId;
    this.initService();
  }

  /**
   * Obtiene el Visitor ID correcto basado en el siteId configurado
   */
  private getCorrectVisitorId(): string | null {
    try {
      // Buscar la cookie que corresponde al siteId actual
      const cookieName = `_pk_id.${this.currentSiteId}.${this.getCookieHash()}`;
      const cookie = this.getCookie(cookieName);

      if (cookie) {
        const parts = cookie.split('.');
        if (parts.length >= 2) {
          return `${parts[0]}.${parts[1]}`;
        }
      }

      // Fallback: buscar cualquier cookie que empiece con _pk_id
      const allCookies = document.cookie.split(';');
      for (const cookie of allCookies) {
        const [name, value] = cookie.trim().split('=');
        if (name.startsWith('_pk_id.') && value) {
          const parts = value.split('.');
          if (parts.length >= 2) {
            console.log(`🔍 Cookie encontrada: ${name} = ${value}`);
            return `${parts[0]}.${parts[1]}`;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('❌ Error obteniendo Visitor ID:', error);
      return null;
    }
  }

  private getCookieHash(): string {
    const domain = window.location.hostname;
    let hash = 0;
    for (let i = 0; i < domain.length; i++) {
      const char = domain.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16).substring(0, 4);
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  /**
   * Inicialización principal del servicio - SIMPLIFICADA
   */
  private async initService(): Promise<void> {
    try {
      this.matomoEnabled = this.MATOMO_CONFIG.enabled;
      this.origen = this.getOrigen();

      const existingVisitorId = this.getCorrectVisitorId();
      if (existingVisitorId) {
        console.log('👤 Visitor ID encontrado en cookies:', existingVisitorId);
        this.visitorType = 'returning';
      } else {
        this.visitorType = localStorage.getItem('onelink_visited')
          ? 'returning'
          : 'new';
      }

      if (this.matomoEnabled) {
        console.log('🚀 Inicializando Matomo...');
        await this.initMatomo();
      }
    } catch (error) {
      console.error('❌ Error inicializando MatomoTrackingService:', error);
    }
  }

  private async initMatomo(): Promise<void> {
    try {
      const nuevoOrigen = this.getOrigen();

      if (!sessionStorage.getItem('onelink_origen')) {
        sessionStorage.setItem('onelink_origen', nuevoOrigen);
        sessionStorage.setItem('onelink_timestamp', Date.now().toString());
      }

      this.origen = sessionStorage.getItem('onelink_origen') || nuevoOrigen;

      if (this.matomoEnabled) {
        window._mtm = window._mtm || [];
        window._mtm.push({
          'mtm.startTime': new Date().getTime(),
          event: 'mtm.Start',
          origen: this.origen,
          visitor_type: this.visitorType,
        });

        window._paq = window._paq || [];

        // CONFIGURACIÓN SIMPLIFICADA - SIN DIMENSIONES
        window._paq.push(['setSiteId', this.currentSiteId]);
        window._paq.push([
          'setTrackerUrl',
          `${this.MATOMO_CONFIG.apiUrl}/matomo.php`,
        ]);

        await this.loadMatomoScript();
      }
    } catch (error) {
      console.error('❌ Error en initMatomo:', error);
    }
  }

  private loadMatomoScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.matomoLoaded) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = this.MATOMO_CONFIG.url;

      script.onload = () => {
        this.matomoLoaded = true;
        console.log('✅ Matomo script cargado.');

        this.initRouterTracking();
        this.trackPageChange(this.router.url);

        setTimeout(() => {
          this.processMatomoInitialization();
        }, 1000);

        resolve();
      };

      script.onerror = (error) => {
        console.error('❌ Error cargando script Matomo:', error);
        this.loadAttempts++;
        if (this.loadAttempts < this.maxLoadAttempts) {
          setTimeout(() => this.loadMatomoScript(), 2000);
        } else {
          reject(error);
        }
      };

      document.head.appendChild(script);
    });
  }

  private processMatomoInitialization(): void {
    try {
      if (window._paq) {
        window._paq.push([
          function (this: any) {
            try {
              const visitorId = this.getVisitorId();
              console.log('🎯 ID obtenido nativamente de Matomo:', visitorId);

              if (visitorId && MatomoTrackingService.instance) {
                MatomoTrackingService.instance.checkBackendLastVisit(visitorId);
              }
            } catch (error) {
              console.warn(
                '⚠️ No se pudo obtener visitorId nativamente, usando método alternativo'
              );
              MatomoTrackingService.instance.fallbackVisitorIdProcess();
            }
          },
        ]);
      } else {
        this.fallbackVisitorIdProcess();
      }
    } catch (error) {
      console.error('❌ Error en processMatomoInitialization:', error);
      this.fallbackVisitorIdProcess();
    }
  }

  private fallbackVisitorIdProcess(): void {
    const visitorId = this.getCorrectVisitorId();
    if (visitorId) {
      console.log('🔄 Usando visitor ID de cookies:', visitorId);
      this.checkBackendLastVisit(visitorId);
    } else {
      console.log('👶 Visitante nuevo, no se encontró visitor ID');
      this.visitorType = 'new';
    }
  }

  /**
   * Consulta al backend recibiendo el ID seguro
   */
  public async checkBackendLastVisit(
    visitorId: string
  ): Promise<TrackingResponse | null> {
    if ((this as any).isCheckingVisit) {
      console.log('⏳ Consulta de visita ya en progreso, omitiendo...');
      return null;
    }

    try {
      (this as any).isCheckingVisit = true;

      if (!visitorId) {
        console.warn('⚠️ ID de visitante vacío. No se consultará el backend.');
        return null;
      }

      const cleanVisitorId = visitorId.split('.')[0];

      const trackingConfig = environment.api.tracking;
      if (!trackingConfig || !trackingConfig.baseUrl) {
        console.warn(
          '⚠️ Configuración de environment.api.tracking incompleta.'
        );
        return null;
      }

      const url = `${trackingConfig.baseUrl}${trackingConfig.endpoints.lastVisit}`;
      const params = new HttpParams().set('visitorId', cleanVisitorId);
      const headers = new HttpHeaders({
        Authorization: `Bearer ${trackingConfig.token}`,
        'Content-Type': 'application/json',
      });

      console.log(
        `📡 Consultando API Tracking: ${url}?visitorId=${cleanVisitorId}`
      );

      const response = await firstValueFrom(
        this.http.get<TrackingResponse>(url, { headers, params })
      );

      console.group('✅ Respuesta Backend (LastVisit)');
      console.log('Datos:', response);
      console.groupEnd();

      if (response && response['data']) {
        this.trackingData = {
          idVisit: response['data'].idVisit,
          visitIp: response['data'].visitIp,
          visitorId: response['data'].visitorId,
          idSite: response['data'].idSite,
        };

        this.visitorType = 'returning';
        localStorage.setItem('onelink_visited', 'true');
        console.log('💾 Datos de tracking guardados:', this.trackingData);
      }

      return response;
    } catch (error: any) {
      console.group('ℹ️ Estado de consulta Backend');
      if (error.status === 404) {
        console.log(
          '👤 Visitante Nuevo (404): No existe registro previo en BD.'
        );
        this.visitorType = 'new';
      } else if (error.status === 401 || error.status === 403) {
        console.warn('🔒 Error de Permisos (Token Inválido):', error.status);
      } else {
        console.error('❌ Error de conexión:', error.message);
      }
      console.groupEnd();
      return null;
    } finally {
      (this as any).isCheckingVisit = false;
    }
  }

  /**
   * Obtiene los datos de tracking cifrados
   */
  public getEncryptedTrackingData(): string {
    if (!this.trackingData) {
      console.warn('⚠️ No hay datos de tracking disponibles');
      return '';
    }

    try {
      if (!env.encryption || !env.encryption.key) {
        console.error(
          '❌ Configuración de encryption no encontrada en environment'
        );
        return '';
      }

      const trackingPayload = {
        v: this.trackingData.visitorId,
        iv: this.trackingData.idVisit,
        ip: this.trackingData.visitIp,
        ts: Date.now(),
        s: this.trackingData.idSite,
      };

      const jsonString = JSON.stringify(trackingPayload);
      const encrypted = CryptoJS.AES.encrypt(
        jsonString,
        env.encryption.key
      ).toString();

      console.log('🔐 Datos de tracking cifrados:', encrypted);
      return encrypted;
    } catch (error) {
      console.error('❌ Error cifrando datos de tracking:', error);
      return '';
    }
  }

  /**
   * DESENCRIPTA datos cifrados - NUEVA FUNCIÓN
   */
  public decryptData(encryptedData: string): any {
    try {
      if (!encryptedData) {
        console.warn('⚠️ No hay datos para desencriptar');
        return null;
      }

      if (!env.encryption || !env.encryption.key) {
        console.error(
          '❌ Configuración de encryption no encontrada en environment'
        );
        return null;
      }

      const decryptedBytes = CryptoJS.AES.decrypt(
        encryptedData,
        env.encryption.key
      );
      const decryptedText = decryptedBytes.toString(CryptoJS.enc.Utf8);

      if (!decryptedText) {
        console.error(
          '❌ Error: Datos desencriptados vacíos o clave incorrecta'
        );
        return null;
      }

      const parsedData = JSON.parse(decryptedText);
      console.log('🔓 Datos desencriptados:', parsedData);

      return parsedData;
    } catch (error) {
      console.error('❌ Error desencriptando datos:', error);
      return null;
    }
  }

  /**
   * Decodifica texto oculto de caracteres invisibles
   */
  public decodeHidden(hiddenStr: string): string {
    if (!hiddenStr) return '';

    try {
      // Remover marcas de inicio y fin
      const cleanStr = hiddenStr.replace(/^\u2060|\u2060$/g, '');
      let result = '';

      // Procesar en bloques de 8 caracteres invisibles (8 bits = 1 byte)
      for (let i = 0; i < cleanStr.length; i += 8) {
        const byteStr = cleanStr.substring(i, i + 8);
        if (byteStr.length < 8) break;

        let binary = '';
        for (let j = 0; j < 8; j++) {
          const char = byteStr[j];
          if (char === '\u200B') {
            binary += '1';
          } else if (char === '\u200C') {
            binary += '0';
          } else {
            console.warn('⚠️ Carácter no reconocido en string oculto');
            break;
          }
        }

        if (binary.length === 8) {
          const charCode = parseInt(binary, 2);
          result += String.fromCharCode(charCode);
        }
      }

      console.log('🔍 Texto oculto decodificado:', result);
      return result;
    } catch (error) {
      console.error('❌ Error decodificando texto oculto:', error);
      return '';
    }
  }

  /**
   * Función completa: Decodifica y desencripta datos ocultos
   */
  public decodeAndDecryptHidden(hiddenStr: string): any {
    try {
      console.log('🕵️‍♂️ Procesando datos ocultos...');

      // 1. Decodificar de caracteres invisibles
      const encryptedData = this.decodeHidden(hiddenStr);

      if (!encryptedData) {
        console.warn('⚠️ No se pudo decodificar el texto oculto');
        return null;
      }

      // 2. Desencriptar los datos
      const decryptedData = this.decryptData(encryptedData);

      return decryptedData;
    } catch (error) {
      console.error(
        '❌ Error en el proceso completo de decodificación:',
        error
      );
      return null;
    }
  }

  /**
   * Codifica el string cifrado en caracteres invisibles
   */
  public encodeHidden(str: string): string {
    if (!str) return '';

    let hiddenStr = '\u2060'; // Marca de inicio
    for (let i = 0; i < str.length; i++) {
      const charCode = str.charCodeAt(i);
      const binary = charCode.toString(2).padStart(8, '0');
      for (let bit of binary) {
        hiddenStr += bit === '1' ? '\u200B' : '\u200C';
      }
    }
    hiddenStr += '\u2060'; // Marca de fin
    return hiddenStr;
  }

  private initRouterTracking(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.trackPageChange(event.urlAfterRedirects);
      });
  }

  private getOrigen(): string {
    const storedOrigen = sessionStorage.getItem('onelink_origen');
    if (storedOrigen) return storedOrigen;

    const params = new URLSearchParams(window.location.search);
    const mtmSource = params.get('mtm_source') || params.get('utm_source');
    const mtmMedium = params.get('mtm_medium') || params.get('utm_medium');

    if (mtmSource && mtmMedium) {
      return `${mtmSource} / ${mtmMedium}`;
    }

    if (document.referrer && document.referrer.length > 0) {
      try {
        const referrerUrl = new URL(document.referrer);
        if (referrerUrl.hostname.indexOf(window.location.hostname) === -1) {
          return referrerUrl.hostname;
        }
      } catch (e) {
        return document.referrer;
      }
    }

    return 'directo';
  }

  private trackPageChange(url: string): void {
    if (!this.matomoLoaded) return;

    const fullUrl = window.location.origin + url;

    if (window._paq) {
      window._paq.push(['setCustomUrl', fullUrl]);
      window._paq.push(['setDocumentTitle', document.title]);
      window._paq.push(['trackPageView']);
      window._paq.push(['enableLinkTracking']);
    }
  }

  // --- Métodos públicos simplificados ---

  public setUserId(userId: string): void {
    if (!this.matomoEnabled || !this.matomoLoaded) return;
    const uid = userId.toString();
    if (window._paq) {
      window._paq.push(['setUserId', uid]);
      window._paq.push(['trackPageView']);
    }
    console.log('👤 UserID establecido:', uid);
  }

  public clearUserId(): void {
    if (!this.matomoEnabled) return;
    if (window._paq) {
      window._paq.push(['resetUserId']);
      window._paq.push(['forgetUser']);
      window._paq.push(['trackPageView']);
    }
  }

  public trackEvent(
    category: string,
    action: string,
    name?: string,
    value?: number
  ): void {
    if (!this.matomoEnabled) return;
    if (window._paq) {
      window._paq.push(['trackEvent', category, action, name, value]);
    }
  }

  public trackServiceButton(config: any): void {
    if (!this.matomoEnabled) return;

    this.trackEvent(
      'Services Section',
      `${config.section}-${config.buttonType}`,
      `${config.serviceTitle}-${config.actionType}`,
      config.buttonNumber
    );

    if (window._mtm) {
      const trackingData = this.trackingData
        ? {
            visitorId: this.trackingData.visitorId,
            idVisit: this.trackingData.idVisit,
            visitIp: this.trackingData.visitIp,
          }
        : {};

      window._mtm.push({
        event: 'service_button_click',
        ...config,
        ...trackingData,
        visitor_type: this.visitorType,
        origen: this.origen,
      });
    }
  }

  public onServiceButtonClick(buttonId: string, buttonText: string): void {
    this.trackEvent('services', 'button_click', `${buttonId}_${buttonText}`, 1);
  }

  /**
   * Obtiene los datos de tracking actuales
   */
  public getTrackingData(): any {
    return this.trackingData;
  }

  /**
   * Verifica si hay datos de tracking disponibles
   */
  public hasTrackingData(): boolean {
    return this.trackingData !== null;
  }

  public enrichClick(destino: string, plan?: string): void {
    if (!this.matomoEnabled) return;

    const destinoFinal = this.extractUtmSource(destino);

    if (window._mtm) {
      window._mtm.push({
        event: 'enriquecer_click',
        origen: this.origen,
        destino: destinoFinal,
        plan: plan || '',
        visitor_type: this.visitorType,
      });
    }
    console.log('🖱️ Click enriquecido:', destinoFinal);
  }

  // También necesitamos el método extractUtmSource que fue eliminado:
  private extractUtmSource(url: string): string {
    try {
      const parsedUrl = new URL(url);
      const utmSource =
        parsedUrl.searchParams.get('utm_source') ||
        parsedUrl.searchParams.get('mtm_source');
      return utmSource || parsedUrl.hostname;
    } catch (e) {
      return url;
    }
  }
}
